import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { ANCHOR_LINK_VARIANT_MAPPINGS } from '../MarketingPageAnchorLink/MarketingPageAnchorLink';
import { DEMOGRAPHIC_INPUTS_DEFAULT_ORDER } from '../../constants';
import { EXTRA_PRODUCT_SPECS_BY_CODE } from '../../constants/product';
import { TermLifeYear } from '../../constants/termLife';
import { QuotationContextValue } from '../../contexts/QuotationContext';
import OptionSlider from '../OptionSlider';
import SumAssured from '../SumAssured';
import TermLifeSlider from '../TermLifeSlider';
import { DemographicInputFields } from './DemographicInputFields';

type Props = Pick<
  QuotationContextValue,
  | 'variables'
  | 'selectedVariable'
  | 'activeProduct'
  | 'setActiveProduct'
  | 'rawProducts'
> & {
  formValues: QuotationContextValue['values'];
  onFormValuesChange: QuotationContextValue['handleChange'];
  onSelectVariable: QuotationContextValue['setSelectedVariable'];
  onVariableAttributeChange: (params: Record<string, string>) => void;
  showTermLifeSlider: boolean;
  termLifeYear: TermLifeYear;
  onTermLifeYearChange: QuotationContextValue['setTermLifeYear'];
  anchorLinkType: keyof typeof ANCHOR_LINK_VARIANT_MAPPINGS;
};

const FormPanel = ({
  activeProduct,
  variables,
  formValues,
  onFormValuesChange,
  selectedVariable,
  onSelectVariable,
  setActiveProduct,
  onVariableAttributeChange,
  rawProducts,
  showTermLifeSlider,
  termLifeYear,
  onTermLifeYearChange,
  anchorLinkType,
}: Props) => {
  const [demographicInputs, shouldShowSumAssured] = useMemo(() => {
    if (!activeProduct) {
      return [DEMOGRAPHIC_INPUTS_DEFAULT_ORDER, false];
    }

    const inputs =
      EXTRA_PRODUCT_SPECS_BY_CODE[activeProduct.code]?.demographicInputs ||
      DEMOGRAPHIC_INPUTS_DEFAULT_ORDER;

    return [
      inputs,
      EXTRA_PRODUCT_SPECS_BY_CODE[activeProduct.code]?.sumAssuredInput,
    ];
  }, [activeProduct]);

  const deductibleVariable = variables
    ? variables.find(
        productAttribute => productAttribute.attribute.key === 'deductible',
      )
    : null;

  const handleFormChange = useCallback(
    (
      event: ChangeEvent<{
        name?: string;
        value: unknown;
      }>,
    ) => {
      const { name = '', value } = event.target;
      onFormValuesChange({ [name]: value as string });
    },
    [onFormValuesChange],
  );

  return (
    <Box px={1}>
      <Grid container direction="column" spacing={2}>
        <DemographicInputFields
          demographicInputs={demographicInputs}
          onValueChange={handleFormChange}
          onVariableAttributeChange={onVariableAttributeChange}
          onFormValuesChange={onFormValuesChange}
          activeProduct={activeProduct}
          setActiveProduct={setActiveProduct}
          rawProducts={rawProducts}
          formValues={formValues}
        />
        {shouldShowSumAssured && (
          <Grid item xs={12}>
            <SumAssured
              age={formValues.age}
              value={formValues.sumAssured}
              onValueChange={handleFormChange}
            />
          </Grid>
        )}
        {deductibleVariable && (
          <Grid item xs={12}>
            <OptionSlider
              productVariable={deductibleVariable}
              selectedVariable={selectedVariable}
              onSelectVariable={onSelectVariable}
            />
          </Grid>
        )}
        {showTermLifeSlider && (
          <Grid item xs={12}>
            <TermLifeSlider
              termLifeYear={termLifeYear}
              onTermLifeYearChange={onTermLifeYearChange}
              showAnchorLink={
                anchorLinkType === 'term-level-life-premium-explanation'
              }
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default FormPanel;
